import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import { LeftChevron } from "@/components/icons";
import apiClient from "@/lib/apiClient";
import {
  buildLiveClassroomPath,
  resolveClassroomJoinTarget,
} from "../utils/jitsi";
import type {
  EducationDetailDto,
  ExamAttemptDto,
  CertificateDto,
  ObjectiveDto,
  EnrollmentDto,
  VirtualClassroomDto,
} from "../types/client-education-detail.types";
import {
  EnrollmentStatus,
  EducationType,
} from "../types/client-education-detail.types";
import { clientEducationDetailApi } from "../services/client-education-detail.api";
import { resolveCta, type CtaDescriptor } from "../utils/resolve-cta";
import { resolveAccessMode } from "../utils/access-mode";
import { DetailSidebar } from "../components/DetailSidebar/DetailSidebar";
import { PendingApprovalState } from "../components/empty-states/PendingApprovalState";
import { RejectedState } from "../components/empty-states/RejectedState";
import { CancelledState } from "../components/empty-states/CancelledState";
import { DatePendingState } from "../components/empty-states/DatePendingState";
import { NotCompletedState } from "../components/empty-states/NotCompletedState";
import { HeroSection } from "../components/sections/HeroSection";
import { DescriptionSection } from "../components/sections/DescriptionSection";
import { EducationContentSection } from "../components/sections/EducationContentSection";
import { ContentSection } from "../components/sections/ContentSection/ContentSection";
import { ContentPreviewSection } from "../components/sections/ContentPreviewSection";
import { VirtualClassroomsSection } from "../components/sections/VirtualClassroomsSection/VirtualClassroomsSection";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import { ResourcesSection } from "../components/sections/ResourcesSection";
import { ClassFilesSection } from "../components/sections/ClassFilesSection";
import { AttendanceSection } from "../components/sections/AttendanceSection";
import { InstructorAttendanceSection } from "../components/sections/InstructorAttendanceSection";
import { ExamSection } from "../components/sections/ExamSection";
import { SurveySection } from "../components/sections/SurveySection";
import { CertificateSection } from "../components/sections/CertificateSection";
import { ClassmatesSection } from "../components/sections/ClassmatesSection";
import { TrainingStages } from "../components/sections/TrainingStages";
import { ActiveStageCard } from "../components/sections/ActiveStageCard";
import styles from "./ClientEducationDetailPage.module.scss";

/**
 * Kursiyer Eğitim Detay Sayfası.
 *
 * Layout mimarisi (wireframe):
 *   DashboardLayout (sol nav daima görünür)
 *   ├─ Breadcrumb + back
 *   ├─ Hero (cover + title + meta) — tam genişlik
 *   ├─ TrainingStages (timeline) — tam genişlik
 *   ├─ 2 kolon grid:
 *   │   ├─ Sticky DetailSidebar (CTA, progress, quick info)
 *   │   └─ Main body (ActiveStageCard + tür bazlı akış + alt bölümler)
 *   └─ Responsive: <1024px tek kolon
 *
 * Tür bazlı akış:
 *   ON_SITE  → SchedulingSection + VirtualClassroomsSection + Attendance
 *   ONLINE   → VirtualClassroomsSection (Jitsi) + Attendance
 *   UNIT     → ContentSection (ünite accordion + inline player)
 *   NONUNIT  → ContentSection (tek akış)
 *
 * Tüm durumlarda gösterilir (eğer veri varsa):
 *   Açıklama, Sınav, Anket, Sertifika, Sınıf Arkadaşları, Kaynaklar
 *
 * Access modes:
 *   catalog   → Hero + Açıklama + Kaynaklar + "Başvur" sidebar
 *   pending   → Hero + Açıklama + Onay Bekleniyor empty state
 *   rejected  → Hero + Açıklama + Reddedildi empty state
 *   cancelled → Hero + Açıklama + İptal Edildi empty state
 *   trainee / completing → Full layout
 *   + Tarih yoksa (startDate/endDate boş): DatePendingState (HeroSection'sız)
 */
export default function ClientEducationDetailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const educationId = params.get("id") ?? "";

  const [loading, setLoading] = useState(true);
  const [education, setEducation] = useState<EducationDetailDto | null>(null);
  const [examAttempts, setExamAttempts] = useState<ExamAttemptDto[]>([]);
  const [certificate, setCertificate] = useState<CertificateDto | null>(null);
  /**
   * "İçeriğe Devam Et" tetikleyicisi. ContentSection bu sayı arttığında kaldığı
   * yerdeki hedefi seçip oynatıcıyı açar; sadece kaydırmak yetmiyordu, kullanıcı
   * bölüme gelip hangi videoyu açacağını kendi bulmak zorunda kalıyordu.
   */
  const [contentAutoSelect, setContentAutoSelect] = useState(0);

  async function refreshDetail() {
    if (!educationId) return;
    try {
      const fresh = await clientEducationDetailApi.getDetail(educationId);
      setEducation(fresh);
    } catch {
      /* ignore */
    }
  }

  const refetchCertificate = useCallback(async () => {
    if (!educationId) return null;
    try {
      const detail = await clientEducationDetailApi.getDetail(educationId);
      const enrollment = detail.enrollment ?? null;
      if (
        !enrollment ||
        enrollment.status !== EnrollmentStatus.COMPLETED
      ) {
        return null;
      }
      const cert = await clientEducationDetailApi.getMyCertificate(
        educationId,
        enrollment.id,
      );
      setEducation(detail);
      setExamAttempts(enrollment.examAttempts ?? []);
      setCertificate(cert);
      return cert;
    } catch {
      return null;
    }
  }, [educationId]);

  useEffect(() => {
    if (!educationId) {
      navigate("/dashboard/client-educations");
      return;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const detail = await clientEducationDetailApi.getDetail(educationId);
        if (cancelled) return;

        const enrollment = detail.enrollment ?? null;
        const needsCert =
          !!enrollment && enrollment.status === EnrollmentStatus.COMPLETED;

        const cert =
          needsCert && enrollment
            ? await clientEducationDetailApi.getMyCertificate(
                educationId,
                enrollment.id,
              )
            : null;

        if (cancelled) return;
        setEducation(detail);
        setExamAttempts(enrollment?.examAttempts ?? []);
        setCertificate(cert);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          toast.error(t("clientEducationDetail.errors.noAccess"));
          navigate("/dashboard/client-educations");
          return;
        }
        if (status === 404) {
          setEducation(null);
        } else {
          toast.error(t("clientEducationDetail.errors.loadFailed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [educationId, navigate]);

  // Pencere/sekme yeniden odaklandığında detay'ı tazele.
  // Senaryo: admin "Yayınla"ya bastıktan sonra kursiyer sekmesi geçmişten
  // gelirken status'u progression'a uğramamış (stale) görüyor — focus ile
  // refetch ederek "halen onay bekliyor" yanlış uyarısını ortadan kaldırırız.
  useEffect(() => {
    if (!educationId) return;
    const onFocus = () => {
      void refreshDetail();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refreshDetail();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationId]);

  // Sertifika hazırlanıyor durumunda otomatik polling.
  // Enrollment COMPLETED olmasına rağmen sertifika henüz hazır değilse
  // sayfayı yenilemeye gerek kalmadan periyodik olarak yeniden çekeriz.
  const certificatePollAttemptsRef = useRef(0);
  useEffect(() => {
    const enrollment = education?.enrollment ?? null;
    if (!enrollment || enrollment.status !== EnrollmentStatus.COMPLETED) {
      certificatePollAttemptsRef.current = 0;
      return;
    }
    if (certificate) {
      certificatePollAttemptsRef.current = 0;
      return;
    }
    if (certificatePollAttemptsRef.current >= 12) return;
    const timer = setTimeout(() => {
      certificatePollAttemptsRef.current += 1;
      void refetchCertificate();
    }, 5000);
    return () => clearTimeout(timer);
  }, [education, certificate, refetchCertificate]);

  /**
   * ContentSection bir hedefi güncellediğinde (izleme ilerlemesi / tamamlandı)
   * detayı yeniden çekmeden yalnızca o hedefi yerinde günceller: tam yenileme
   * oynatıcıyı sıfırlar ve video baştan başlardı.
   */
  function handleObjectiveUpdated(updated: ObjectiveDto) {
    setEducation((prev) =>
      prev
        ? {
            ...prev,
            chapters: (prev.chapters ?? []).map((chapter) => ({
              ...chapter,
              objectives: (chapter.objectives ?? []).map((o) =>
                o.id === updated.id ? updated : o,
              ),
            })),
          }
        : prev,
    );
  }

  function handleEnrollmentProgressUpdated(progressPercent: number) {
    setEducation((prev) =>
      prev?.enrollment
        ? { ...prev, enrollment: { ...prev.enrollment, progressPercent } }
        : prev,
    );
  }

  function handleJump(sectionId: string) {
    // İçerik oynatıcısı yalnızca kayıtlı kullanıcıya ve ünite taşıyan eğitimde
    // render edilir; katalog/onay bekleme modunda aynı yere serbest metin
    // bölümü düşer. Hedef bulunamazsa metne kaydırıyoruz ki "İçeriğe Devam Et"
    // hiçbir modda tepkisiz kalmasın.
    const target =
      document.getElementById(sectionId) ??
      (sectionId === "education-content"
        ? document.getElementById("education-content-text")
        : null);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /**
   * Tıklanan sanal sınıfın Jitsi oturumunu yeni sekmede açar.
   *
   * ÖNEMLİ — URL ve token AYNI sınıftan gelmelidir. Jitsi JWT'si `room` claim'ine
   * bağlıdır; eğitim seviyesindeki JITSI objective'i tek bir odaya (eskiden hep
   * ilk sınıfa) işaret ettiği için, ikinci/üçüncü sınıfa tıklayan kullanıcı ya
   * yanlış odaya düşüyor ya token reddediliyordu. Bu yüzden önce sınıfın kendi
   * `jitsiUrl`/`jitsiToken` çifti kullanılır; ikisi de yoksa objective'e düşülür.
   *
   * Hem kursiyer hem eğitmen görünümü bu handler'ı paylaşır — ayrı kopya tutmak,
   * token mantığı değiştiğinde birinin geride kalmasına yol açardı.
   */
  async function handleJoinVirtualClassroom(vc: VirtualClassroomDto) {
    if (!education) return;

    // Eğitici başlatma kapısı:
    // - Eğitmen "katıl" dediğinde ders başlatılır (aktif oturum açılır) — kursiyer
    //   kapısı bu oturumun varlığına bakar.
    // - Kursiyer için backend saat penceresi + "eğitmen başlattı mı" kontrolü
    //   yapar; dışındaysa 400 döner (mesajı global toast gösterir) ve oda açılmaz.
    try {
      if (accessMode === "instructor") {
        await virtualClassroomsApi.startLiveSession(vc.id);
        // Oturum açıldı: detayı tazeleyip `hasActiveSession`'ı güncelliyoruz.
        // Bu bayrak hem eğitmenin butonunu "Dersi Başlat"tan "Katıl"a çevirir
        // hem kursiyer kartlarının kapısını açar; tazelemezsek eğitmen sayfayı
        // elle yenileyene kadar dersi başlatmamış gibi görünürdü.
        void clientEducationDetailApi
          .getDetail(education.id)
          .then(setEducation)
          .catch(() => {
            /* tazeleme başarısızsa oda yine açılıyor; sessiz geç */
          });
      } else {
        await virtualClassroomsApi.getClientJitsiConfig(vc.id);
      }
    } catch {
      // Hata mesajı apiClient interceptor toast'u ile gösterildi.
      return;
    }

    // URL/token çifti resolveClassroomJoinTarget'ta seçilir; bağlantı yoksa açma.
    if (!resolveClassroomJoinTarget(education, vc)) return;

    // Ders doğrudan Jitsi adresinde değil portalın canlı sınıf sayfasında açılır:
    // Jitsi orada gömülü çalışır, giriş-çıkış süresi yoklama için kaydedilir ve
    // sınıf belgeleri yanında listelenir.
    window.open(
      buildLiveClassroomPath(education.id, vc.id),
      "_blank",
      "noopener",
    );
  }

async function handleReapply() {
    if (!education) return;
    try {
      await apiClient.post(
        `/api/client/educations/${education.id}/enrollments/request`,
      );
      toast.success(t("clientEducationDetail.errors.applySuccess"));
      const fresh = await clientEducationDetailApi.getDetail(education.id);
      setEducation(fresh);
    } catch {
      toast.error(t("clientEducationDetail.errors.applyFailed"));
    }
  }

  function handleCtaActivate(action: CtaDescriptor["action"]) {
    if (!education) return;
    switch (action.kind) {
      case "reapply":
        void handleReapply();
        break;
      case "joinVirtualClassroom": {
        const vc = education.virtualClassrooms?.find(
          (v) => v.id === action.classroomId,
        );
        // Sanal sınıf kartıyla aynı handler: URL + token birlikte seçilir.
        // Bu dal eskiden token'sız açıyordu; Jitsi de kullanıcıya ad/şifre
        // soruyordu. Sınıf bulunamazsa listeye kaydır.
        if (vc) {
          void handleJoinVirtualClassroom(vc);
        } else {
          handleJump("virtualClassrooms");
        }
        break;
      }
      case "scrollTo":
        handleJump(action.sectionId);
        break;
      case "continueContent":
        setContentAutoSelect((n) => n + 1);
        handleJump("education-content");
        break;
      case "goExam":
        navigate(`/dashboard/client-exams/take/${education.id}`);
        break;
      case "goSurvey":
        // ClientPollsPage location.state.educationId okuyup modalı otomatik açar
        navigate("/dashboard/client-polls", {
          state: {
            educationId: education.id,
            pollTitle: education.name,
          },
        });
        break;
      case "openCertificate":
        handleJump("certificate");
        if (!certificate) {
          void refetchCertificate();
        }
        break;
      default:
        break;
    }
  }

  /** Tüm state'lerde kullanılan üst bar (back + breadcrumb). */
  const renderTopBar = (title?: string) => (
    <div className={styles.topBar}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate(-1)}
        aria-label={t("clientEducationDetail.back")}
      >
        <LeftChevron width={18} height={18} />
      </button>
      <button
        type="button"
        className={styles.breadcrumbLink}
        onClick={() => navigate("/dashboard/client-educations")}
      >
        {t("clientEducationDetail.breadcrumbHome")}
      </button>
      <span className={styles.breadcrumbSep}>/</span>
      <span className={styles.breadcrumbCurrent}>{title ?? t("clientEducationDetail.title")}</span>
    </div>
  );

  const renderWithLayout = (content: React.ReactNode, title?: string) => (
    <DashboardLayout>
      <Header title={t("clientEducationDetail.title")} />
      <div className={styles.page}>
        {renderTopBar(title)}
        {content}
      </div>
    </DashboardLayout>
  );

  // ---------- Loading / Not Found ----------
  if (loading) {
    return renderWithLayout(
      <div className={styles.loading}>{t("loading")}</div>,
    );
  }

  if (!education) {
    return renderWithLayout(
      <div className={styles.empty}>{t("educationCatalog.errors.notFound")}</div>,
    );
  }

  const enrollment = education.enrollment ?? null;
  let accessMode = resolveAccessMode(enrollment);

  // "Eğitimde yayınla demeden kursiyer eğitim içindeki bilgileri görmesin":
  // Eğitim henüz kursiyere yayına alınmadıysa (backend isContentPublished=false —
  // admin "Yayınla" demedi / talep toplama hâlâ açık), kursiyer ENROLLED veya
  // içerik sürecinde olsa bile eğitime "düşmemiştir"; başvuru/onay süreci
  // görünümünde kalır ve içerik (chapter/objective, sanal sınıf, sınav vb.)
  // gösterilmez. Backend içeriği zaten strip'ler; burada da tam içerik
  // layout'una düşmeyi engelliyoruz.
  // Kaydı onaylanmış kursiyere "Başvurun onay bekliyor" demek yanlıştı: başvuru
  // zaten onaylı, eksik olan yöneticinin "Yayınla" adımı. Bu ayrımı bayrakla
  // taşıyıp boş durumda doğru metni gösteriyoruz.
  let isAwaitingPublish = false;
  if (
    education.isContentPublished === false &&
    (accessMode === "trainee" || accessMode === "completing")
  ) {
    accessMode = "pending";
    isAwaitingPublish = true;
  }

  // Eğitmenin enrollment kaydı yok; yalnız enrollment'a bakan resolveAccessMode
  // onu "catalog" moduna düşürüyor ve sanal sınıf bölümü hiç render edilmediği
  // için canlı derse katılamıyordu. Backend `isInstructor` ile bunu bildiriyor.
  // Kaydı olan kullanıcının kendi kursiyer akışı korunur — sadece kayıtsız
  // eğitmen bu moda geçer.
  if (education.isInstructor === true && !enrollment) {
    accessMode = "instructor";
  }

  // ---------- Tarih Bekleniyor ----------
  // Eğitimin kendi tarih alanları boş olsa bile, kursiyerin atandığı bir sanal
  // sınıfın (backend taraflı `participants: some(userID)` ile zaten filtrelenmiş)
  // startDate/endDate'i varsa tarih belirlenmiş kabul edilir.
  const hasClassDates = (education.virtualClassrooms ?? []).some(
    (vc) => !!vc.startDate || !!vc.endDate,
  );
  const isDatePending =
    !education.startDate && !education.endDate && !hasClassDates;
  if (
    isDatePending &&
    accessMode !== "pending" &&
    accessMode !== "rejected" &&
    accessMode !== "cancelled"
  ) {
    // Talep toplama penceresi: flag true + bugün aralıkta
    const startOfDayMs = (v: string | undefined | null) => {
      if (!v) return null;
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    };
    const _today = new Date();
    const todayMs = new Date(
      _today.getFullYear(),
      _today.getMonth(),
      _today.getDate(),
    ).getTime();
    const reqStartMs = startOfDayMs(education.requestCollectionStartDate);
    const reqEndMs = startOfDayMs(education.requestCollectionEndDate);
    const hasReqWindow = reqStartMs != null || reqEndMs != null;
    const withinReqWindow =
      (reqStartMs == null || todayMs >= reqStartMs) &&
      (reqEndMs == null || todayMs <= reqEndMs);
    const requestCollectionOpen =
      education.isAcceptingRequestCollection === true &&
      hasReqWindow &&
      withinReqWindow;

    const pendingExtra = enrollment ? (
      <Chip type="warning" variant="filled" size="medium">
        Talep Gönderildi
      </Chip>
    ) : requestCollectionOpen ? (
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleReapply}
      >
        Talep Et
      </Button>
    ) : undefined;

    return renderWithLayout(
      <>
        {education.description && <DescriptionSection education={education} />}
        <EducationContentSection education={education} />
        <ContentPreviewSection education={education} />
        <DatePendingState extra={pendingExtra} />
        {/* Public dokümanlar tarih henüz atanmamış olsa da erişilebilir olabilir */}
        <ClassFilesSection educationId={education.id} />
        <ResourcesSection education={education} />
      </>,
      education.name,
    );
  }

  // ---------- CTA ----------
  // `resolveCta` enrollment=null durumunu da yönetiyor (talep toplama açıksa
  // "Başvur" primary, kapalıysa disabled + hint). Bu yüzden her zaman çağırıp
  // tek doğruluk kaynağı üzerinden besliyoruz.
  const cta = resolveCta({ education, enrollment, examAttempts });

  // Sidebar içeriği — enrollment yoksa placeholder ama daima render edilir.
  const sidebarNode = (
    <DetailSidebar
      education={education}
      enrollment={
        enrollment ??
        ({
          id: "",
          educationID: education.id,
          userID: "",
          status: 0,
          progressPercent: 0,
        } as EnrollmentDto)
      }
      cta={cta}
      onCtaActivate={handleCtaActivate}
      onJumpSection={handleJump}
    />
  );

  // ---------- Devamsızlıktan / "tamamlamadı" ----------
  // Üstte kırmızı banner; timeline tüm aşamaları "failed" gösterir; CTA gizli.
  // Açıklama ve dokümanlar erişilebilir kalmalı (geçmişe referans).
  if (accessMode === "notCompleted") {
    return renderWithLayout(
      <>
        <NotCompletedState />
        <HeroSection education={education} />
        <TrainingStages
          education={education}
          enrollment={enrollment}
          onJump={handleJump}
        />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            <DescriptionSection education={education} />
            <ClassFilesSection educationId={education.id} />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Pending / Rejected / Cancelled ----------
  // Hero + TrainingStages + sidebar + empty state + resources
  if (
    accessMode === "pending" ||
    accessMode === "rejected" ||
    accessMode === "cancelled"
  ) {
    const emptyState =
      accessMode === "pending" ? (
        <PendingApprovalState awaitingPublish={isAwaitingPublish} />
      ) : accessMode === "rejected" ? (
        <RejectedState onReapply={() => void handleReapply()} />
      ) : (
        <CancelledState onReapply={() => void handleReapply()} />
      );

    return renderWithLayout(
      <>
        <HeroSection education={education} />
        <TrainingStages
          education={education}
          enrollment={enrollment}
          onJump={handleJump}
        />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            {emptyState}
            <DescriptionSection education={education} />
            <EducationContentSection education={education} />
            <ClassFilesSection educationId={education.id} />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Eğitmen ----------
  // Eğitmenin enrollment'ı yoktur: sınav / anket / sertifika gibi kursiyere özel
  // adımlar gösterilmez. Buna karşılık eğitim içeriğini ve atandığı sanal
  // sınıfları görmesi, canlı derse katılabilmesi ve YOKLAMA ALABİLMESİ gerekir —
  // yoklama kursiyerin kendi katılımını gördüğü ekran değil, eğitmenin
  // doldurduğu ekrandır (bkz. InstructorAttendanceSection).
  if (accessMode === "instructor") {
    const instructorSessionType =
      education.type === EducationType.ON_SITE ||
      education.type === EducationType.ONLINE;

    return renderWithLayout(
      <>
        <HeroSection education={education} />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            <DescriptionSection education={education} />
            <EducationContentSection education={education} />
            {instructorSessionType && (
              <VirtualClassroomsSection
                education={education}
                educationType={education.type}
                isInstructor
                onJoin={handleJoinVirtualClassroom}
              />
            )}
            {/* Yoklama oturumlu eğitimlere özgüdür: üniteli/ünitesiz (kendi
                hızında) eğitimde katılım günü diye bir kavram yok. */}
            {instructorSessionType && (
              <InstructorAttendanceSection
                educationId={education.id}
                educationName={education.name}
              />
            )}
            <ClassFilesSection
              educationId={education.id}
              uploadClassrooms={(education.virtualClassrooms ?? []).map(
                (c) => ({ id: c.id, name: c.name }),
              )}
            />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Catalog (henüz kayıt yok) ----------
  if (accessMode === "catalog") {
    return renderWithLayout(
      <>
        <HeroSection education={education} />
        <TrainingStages
          education={education}
          enrollment={enrollment}
          onJump={handleJump}
        />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            <ActiveStageCard
              education={education}
              enrollment={enrollment}
              cta={cta}
              onCtaActivate={handleCtaActivate}
              onJump={handleJump}
            />
            <DescriptionSection education={education} />
            <EducationContentSection education={education} />
            <ContentPreviewSection education={education} />
            <ClassFilesSection educationId={education.id} />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Trainee / Completing — full layout ----------
  const isSessionType =
    education.type === EducationType.ON_SITE ||
    education.type === EducationType.ONLINE;
  // Ünite/hedef taşıyan eğitim mi. Türe değil içeriğe bakıyoruz: üniteli
  // (UNIT) ve ünitesiz (NONUNIT) eğitimlerin tamamı buraya girer, ayrıca
  // içerik eklenmiş oturumlu eğitimler de oynatıcıyı görür.
  const hasContentObjectives = (education.chapters ?? []).some(
    (c) => (c.objectives ?? []).length > 0,
  );

  return renderWithLayout(
    <>
      <HeroSection education={education} />
      <TrainingStages
        education={education}
        enrollment={enrollment}
        onJump={handleJump}
      />

      <div className={styles.layout}>
        <aside className={styles.sidebar}>{sidebarNode}</aside>

        <main className={styles.main}>
          {/* Aktif Aşama — "şu an ne yapmalıyım" kartı (tipe göre dinamik) */}
          <ActiveStageCard
            education={education}
            enrollment={enrollment}
            cta={cta}
            onCtaActivate={handleCtaActivate}
            onJump={handleJump}
          />

          {/* Açıklama — tüm türlerde */}
          <DescriptionSection education={education} />
          <EducationContentSection education={education} />

          {/*
            Ünite/hedef içeriği — video oynatıcı, ünite akordiyonu, canlı ders
            gömülü penceresi. Bu bölüm daha önce hiç render EDİLMİYORDU: sayfa
            yalnızca `EducationContentSection`'ı (serbest metin) basıyor,
            "İçeriğe Devam Et" de onun anchor'ına kaydırıyordu. Sonuç: üniteli
            eğitimde kursiyer içeriği hiç açamıyordu.

            Kayıt gerektirir (ilerleme uçları enrollment ID'siyle çalışır) ve
            yalnızca hedef taşıyan eğitimlerde anlamlıdır.
          */}
          {enrollment && hasContentObjectives && (
            <ContentSection
              autoSelectTrigger={contentAutoSelect}
              education={education}
              enrollment={enrollment}
              onEnrollmentProgressUpdated={handleEnrollmentProgressUpdated}
              onObjectiveUpdated={handleObjectiveUpdated}
              onRequestRefresh={refreshDetail}
            />
          )}
          {/* ON_SITE ve ONLINE: Sanal sınıf / fiziksel sınıf oturum kartları */}
          {isSessionType && (
            <VirtualClassroomsSection
              education={education}
              educationType={education.type}
              onJoin={handleJoinVirtualClassroom}
            />
          )}

          {/* Yoklama — sadece session-tabanlı eğitimler için */}
          {isSessionType && enrollment && (
            <AttendanceSection education={education} enrollment={enrollment} />
          )}

          {/* Sınav — eğitimde sınav tanımlanmışsa */}
          {enrollment && education.exam?.id && (
            <ExamSection
              education={education}
              enrollment={enrollment}
              attempts={examAttempts}
              onStart={() =>
                navigate(`/dashboard/client-exams/take/${education.id}`)
              }
            />
          )}

          {/* Anket — eğitimde anket tanımlanmışsa */}
          {enrollment && education.survey && (
            <SurveySection
              education={education}
              enrollment={enrollment}
              onStart={({ done }) =>
                navigate("/dashboard/client-polls", {
                  state: {
                    educationId: education.id,
                    pollTitle: education.name,
                    status: done ? "completed" : "pending",
                  },
                })
              }
            />
          )}

          {/* Sertifika — her durumda göster, içi durumuna göre bilgi/aksiyon verir */}
          {enrollment && (
            <CertificateSection
              education={education}
              enrollment={enrollment}
              certificate={certificate}
            />
          )}

          {/* Sınıf Arkadaşları — ON_SITE / ONLINE'da ve kayıt varsa */}
          {enrollment && isSessionType && (
            <ClassmatesSection
              education={education}
              currentUserId={enrollment.userID}
            />
          )}

          {/* Public dokümanlar — tüm türlerde, kayıt olsun olmasın */}
          <ClassFilesSection educationId={education.id} />
          <ResourcesSection education={education} />
        </main>
      </div>
    </>,
    education.name,
  );
}
